import React, { useState, useEffect, useRef } from "react";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";

const OutingChat = ({ outing, user, onClose, senderType = "Support" }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef();

  const fetchChat = async () => {
    try {
      const res = await apiService.getAll(`${config.GETOUTINGCHATBYID}?outingId=${outing.outingId}`);
      if (res && res.messages) {
        setMessages(res.messages);
      }
    } catch (err) { 
        console.error("Admin Chat load error", err); 
    }
  };

  useEffect(() => {
    if (outing) {
      fetchChat();
    }
  }, [outing]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setLoading(true);
    const payload = {
      // Accessing user.id and user.name directly from the Auth object passed in
      senderId: user?.id, 
      senderName: user?.name || "Support Team", // Dynamically using user.name
      senderType: "Support", // "Support"
      content: newMessage,
      attachments: []
    };

    try {
      await apiService.create(`${config.CREATECHATMESSAGE}?outingId=${outing.outingId}`, payload);
      setNewMessage("");
      await fetchChat(); 
    } catch (err) { 
        console.error("Admin Send error", err); 
    } finally { 
        setLoading(false); 
    }
  };

  return (
    <div className="card shadow-lg border-0 h-100" style={{ minHeight: '550px', display: 'flex', flexDirection: 'column' }}>
      
      {/* CHAT HEADER */}
      <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3">
        <div>
            <h6 className="mb-0 text-white fw-bold">
                <i className="ri-customer-service-2-line me-2"></i> Support Desk
            </h6>
            <small className="text-white-50">Inquiry: {outing.destination}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
            <span className="badge bg-soft-light text-white border border-light-subtle fs-10">{user?.name}</span>
            <button className="btn btn-close btn-close-white" onClick={onClose}></button>
        </div>
      </div>

      {/* MESSAGE LIST AREA */}
      <div className="card-body p-3 bg-light-subtle" style={{ flex: '1', overflowY: "auto", maxHeight: '420px' }}>
        {messages.length > 0 ? messages.map((msg, index) => (
          <div key={index} className={`d-flex mb-3 ${msg.senderType === senderType ? "justify-content-end" : "justify-content-start"}`}>
            <div 
              className={`p-2 px-3 rounded-3 shadow-sm ${msg.senderType === senderType ? "bg-success text-white" : "bg-white text-dark border"}`}
              style={{ maxWidth: "85%", fontSize: "13.5px" }}
            >
              <div className="fw-bold small mb-1" style={{ opacity: 0.8 }}>
                  {/* If senderType matches ours, say "You", else show the name stored in the message */}
                  {msg.senderType === senderType ? `You (${user?.name || "Support"})` : msg.senderName}
              </div>
              <div className="lh-sm">{msg.content}</div>
              <div className="text-end mt-1" style={{ fontSize: "9px", opacity: 0.6 }}>
                  {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        )) : (
          <div className="text-center mt-5 text-muted">
              <i className="ri-question-answer-line display-4 opacity-25"></i>
              <p className="mt-2">No conversation history yet.</p>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* INPUT BAR */}
      <div className="card-footer bg-white border-top p-3">
        <form onSubmit={handleSendMessage} className="d-flex gap-2">
          <input 
            type="text" 
            className="form-control border-light-subtle" 
            placeholder="Type a response to client..." 
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={loading}
          />
          <button className="btn btn-success" type="submit" disabled={loading || !newMessage.trim()}>
             {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="ri-send-plane-2-fill"></i>}
          </button>
        </form>
        <div className="mt-2 text-center">
           <small className="text-muted fs-11">Messaging as <b>{user?.name || "Support"}</b></small>
        </div>
      </div>
    </div>
  );
};

export default OutingChat;